import Base from "formiojs/components/_classes/component/Component";
import editForm from "formiojs/components/table/Table.form";
import "viewerjs/dist/viewer.css";
import Viewer from "viewerjs";
import $ from "jquery";
import { Formio } from "formiojs";
import * as _lodash from "lodash";

export default class DocumentSignerComponent extends Base {
    // Constants for document statuses
    static DOCUMENT_STATUS = {
        UNSIGNED: "UNSIGNED",
        READY_FOR_SIGNATURE: "READY_FOR_SIGNATURE",
        FINALIZED: "FINALIZED",
        CANCELLED: "CANCELLED",
        SIGNED: "SIGNED"
    };

    // Constants for polling interval
    static POLLING_INTERVAL = 1000;

    constructor(component, options, data) {
        const formOptions = Formio.getPlugin("optionsPlugin");
        const customOptions = _lodash.default.merge(options, formOptions.options);
        
        if (!customOptions.core) {
            console.warn('DocumentSigner: No core provided in options');
        }
        
        super(component, customOptions, data);
        
        // Initialize component properties
        component.appId = customOptions.appId;
        this.osjsCore = customOptions.core;
        this.form = this.getRoot();
        this.signCompleted = false;
        this.documentsList = [];
        this.eSignerFinalizeCallback = options.eSignerFinalizeCallback;
        this.previousValue = null;
    }
        
    static schema(...extend) {
        return Base.schema({
                label: "documentsigner",
                type: "documentsigner",
            },
            ...extend
        );
    }
    
    setValue(value) {
        this.value = JSON.stringify(value);
        this.dataValue = JSON.stringify(value);
    }
    
    get defaultSchema() {
        return DocumentSignerComponent.schema();
    }
    
    static builderInfo = {
        title: "Document Signer",
        group: "basic",
        icon: "fa fa-file",
        weight: 70,
        schema: DocumentSignerComponent.schema(),
    };

    /**
     * Get file URL and icon based on file type
     * @param {Object} file - File object
     * @param {string} type - File type/extension
     * @returns {Object} Object containing url and icon
     */
    getUrl(file, type) {
        if (!file || !file.url) {
            console.warn('DocumentSigner: Invalid file object or missing URL');
            return { url: '', icon: "<i class='fa fa-file-o fileIcon'></i>" };
        }
        
        const url = file.url;
        const icon = this.getFileIcon(type);
        
        return { url, icon };
    }

    /**
     * Get appropriate icon for file type
     * @param {string} type - File type/extension
     * @returns {string} HTML icon string
     */
    getFileIcon(type) {
        const iconMap = {
            'pdf': "<i class='fa fa-file-pdf-o'></i>",
            'png': "<i class='fa fa-picture-o'></i>",
            'jpeg': "<i class='fa fa-picture-o'></i>",
            'jpg': "<i class='fa fa-picture-o'></i>"
        };
        
        return iconMap[type] || "<i class='fa fa-file-o fileIcon'></i>";
    }

    /**
     * Get file type/extension from file object
     * @param {Object} file - File object
     * @returns {string} File extension in lowercase
     */
    getType(file) {
        if (!file || !file.originalName) {
            return '';
        }
        
        const lastDotIndex = file.originalName.lastIndexOf(".");
        return lastDotIndex !== -1 
            ? file.originalName.substr(lastDotIndex + 1).toLowerCase()
            : '';
    }

    /**
     * Get file name from form data
     * @returns {string|null} File name or null if not found
     */
    getName() {
        const form = this.form || this;
        
        try {
            const documentsList = this.component.documentsList;
            const index = this.component.index;
            
            if (!form._data || !form._data[documentsList] || !form._data[documentsList][index]) {
                console.warn('DocumentSigner: Invalid form data structure');
                return null;
            }
            
            const name = form._data[documentsList][index].originalName;
            if (name) {
                this.dataValue = name;
            }
            return name;
        } catch (error) {
            console.error('DocumentSigner: Error getting file name:', error);
            this.redraw();
            return null;
        }
    }

    /**
     * Generate HTML for document list based on current file data
     */
    bindHandlers() {
        const name = this.getName();
        if (!name) {
            this.signCompleted = true;
            return;
        }
        
        // Skip if data hasn't changed
        if (this.previousValue === this.dataValue) {
            return;
        }
        this.previousValue = this.dataValue;
        
        const form = this.form || this;
        const file = form._data[this.component.documentsList][this.component.index];
        const type = this.getType(file);
        const { url, icon } = this.getUrl(file, type);
        
        this.documentsList = this.generateDocumentHTML(file, type, url, icon, name);
        this.redraw();
    }

    /**
     * Generate complete HTML for document display
     * @param {Object} file - File object
     * @param {string} type - File type
     * @param {string} url - File URL
     * @param {string} icon - File icon HTML
     * @param {string} name - File name
     * @returns {string} Complete HTML string
     */
    generateDocumentHTML(file, type, url, icon, name) {
        const readOnlyStyle = this.options?.readOnly ? 'opacity:.5;pointer-events:none' : '';
        const documentInfo = this.generateDocumentInfo(file, type, url, icon, name);
        const actionButtons = this.generateActionButtons(file);
        const previewModal = this.generatePreviewModal();
        const signModal = this.generateSignModal();
        
        return `
            <div class="docList" style="margin:0;${readOnlyStyle}" key="${this.id}">
                <div class="fileDiv">
                    <div class="singleFile row" data-file="${this.id}" data-type="${type}" data-url="${url}?action=preview">
                        ${documentInfo}
                        ${actionButtons}
                    </div>
                </div>
            </div>
            ${previewModal}
            ${signModal}
        `;
    }

    /**
     * Generate document info section (icon + name)
     * @param {Object} file - File object
     * @param {string} type - File type
     * @param {string} url - File URL
     * @param {string} icon - File icon HTML
     * @param {string} name - File name
     * @returns {string} Document info HTML
     */
    generateDocumentInfo(file, type, url, icon, name) {
        return `<span class="fileName col-md-9 doc-text-align">${icon}&nbsp;&nbsp;${name}</span>`;
    }

    /**
     * Generate action buttons based on file status
     * @param {Object} file - File object
     * @returns {string} Action buttons HTML
     */
    generateActionButtons(file) {
        const viewButton = `
            <button class="btn btn-sm btn-info ${this.component.key}-selectFile viewhide" 
                    title="Click to preview the document">
                <i class="fa fa-eye"></i>
            </button>
        `;
        
        const signButton = this.generateSignButton(file);
        
        return `<span class="col-md-3">${viewButton}${signButton}</span>`;
    }

    /**
     * Generate sign button based on file status
     * @param {Object} file - File object
     * @returns {string} Sign button HTML
     */
    generateSignButton(file) {
        const { UNSIGNED, READY_FOR_SIGNATURE } = DocumentSignerComponent.DOCUMENT_STATUS;
        const isUnsigned = file.status === UNSIGNED || file.status === READY_FOR_SIGNATURE;
        
        if (isUnsigned) {
            this.signCompleted = false;
            return `
                <button class="btn-align btn btn-sm btn-info ${this.component.key}-signFile" 
                        id="sign_btn_${this.id}" 
                        style="margin-left:5px;" 
                        title="Click to sign the document">
                    <i class="fal fa-file-signature"></i>
                </button>
            `;
        } else {
            this.signCompleted = true;
            this.eSignerFinalizeCallback?.();
            return `
                <button class="btn btn-sm btn-success btn-align" 
                        style="margin-left:5px;" 
                        title="Already Signed">
                    eSign Completed
                </button>
            `;
        }
    }

    /**
     * Generate preview modal HTML
     * @returns {string} Preview modal HTML
     */
    generatePreviewModal() {
        return `
            <div id="${this.component.key}-filePreviewModal" class="modal">
                <div style="height:inherit;display: block;background-color: white;">
                    <div id="${this.component.key}-closeFile" class="viewer-button viewer-close" style="z-index:111"></div>
                    <div id="${this.component.key}-filePreviewWindow" style="height:inherit"></div>
                </div>
            </div>
        `;
    }

    /**
     * Generate sign modal HTML
     * @returns {string} Sign modal HTML
     */
    generateSignModal() {
        return `<div id="myModal_${this.id}" class="insuresign modal"></div>`;
    }

    displayImage(show, src) {
        if (show) {
            this.imageViewer ? this.imageViewer.destroy() : null;
            this.imageViewer = new Viewer(document.getElementById(src), {
                inline: true,
                button: false,
                navbar: false,
                toolbar: {
                    zoomIn: true,
                    zoomOut: true,
                    oneToOne: true,
                    reset: true,
                    rotateLeft: true,
                    rotateRight: true,
                    flipHorizontal: true,
                    flipVertical: true,
                },
            });
        } else {
            this.imageViewer ? this.imageViewer.destroy() : null;
        }
    }

    attach(element) {
        var that = this;
        if (document.getElementById("sign_btn_" + this.id)) {
            document
                .getElementById("sign_btn_" + this.id)
                .addEventListener("click", async() => {
                    var signingLink = this.form._data[this.component.documentsList][
                        this.component.index
                    ].signingLink;
                    this.showInsureSignModal(
                        this.component.documentsList + this.component.index,
                        signingLink
                    );
                    this.pollForStatus();
                });
        }
        if (element) {
            var elements = element.getElementsByClassName(that.key + "-selectFile");
            Array.from(elements).forEach(function(ele) {
                ele.addEventListener("click", function(event) {
                    var file = this.parentElement.parentElement.getAttribute("data-file");
                    var url = this.parentElement.getAttribute("data-url");
                    if (!url) {
                        url = this.parentElement.parentElement.getAttribute("data-url");
                    }
                    var fileType = this.parentElement.parentElement
                        .getAttribute("data-type")
                        .toLowerCase();
                    if (fileType == "png" || fileType == "jpeg" || fileType == "jpg") {
                        document.getElementById(that.key + "-filePreviewWindow").innerHTML =
                            '<img id="imagesPreview" src="' +
                            url +
                            '" style="display:none;" key="' +
                            url +
                            '"></img>';
                        that.displayImage(true, that.key + "-filePreviewWindow");
                    }
                    window.open(url, "_blank");
                    event.stopPropagation();
                });
            });

            var downloadElements = element.getElementsByClassName(
                that.key + "-downloadFile"
            );
            Array.from(downloadElements).forEach(function(ele) {
                ele.addEventListener("click", function(event) {
                    var url = this.parentElement.getAttribute("data-url");
                    if (!url) {
                        url = this.parentElement.parentElement.getAttribute("data-url");
                    }
                    window.open(url, "_blank");
                    event.stopPropagation();
                });
            });
            var closeFile = document.getElementById(that.key + "-closeFile");
            if (closeFile) {
                closeFile.addEventListener("click", function(event) {
                    that.displayImage(false);
                    document.getElementById(
                        that.key + "-filePreviewModal"
                    ).style.display = "none";
                });
            }
        }
        return super.attach(element);
    }

    /**
     * Open signing URL in new window
     * @param {string} docid - Document ID (for future use)
     * @param {string} signing_url - URL to open for signing
     */
    showInsureSignModal(docid, signing_url) {
        if (!signing_url) {
            console.error('DocumentSigner: No signing URL provided');
            return;
        }
        
        try {
            window.open(signing_url, "_blank");
        } catch (error) {
            console.error('DocumentSigner: Failed to open signing URL:', error);
        }
    }

    /**
     * Poll for document signing status and update UI accordingly
     */
    pollForStatus() {
        setTimeout(() => {
            this.checkSigningStatus()
                .then(status => {
                    if (this.isSigningComplete(status)) {
                        this.handleSigningComplete();
                    } else {
                        this.pollForStatus();
                    }
                })
                .catch(error => {
                    console.error('DocumentSigner: Error checking signing status:', error);
                    // Continue polling on error
                    this.pollForStatus();
                });
        }, DocumentSignerComponent.POLLING_INTERVAL);
    }

    /**
     * Check current signing status from server
     * @returns {Promise<string>} Promise resolving to status string
     */
    async checkSigningStatus() {
        const form = this.form || this;
        const docId = form._data[this.component.documentsList][this.component.index].docId;
        
        if (!docId) {
            throw new Error('No document ID found');
        }
        
        const helper = this.osjsCore.make("oxzion/restClient");
        const response = await helper.request("v1", `/gateway/public/workflow/esignstatus?docId=${docId}`, {}, "get");
        return response.data.status;
    }

    /**
     * Check if signing process is complete
     * @param {string} status - Current signing status
     * @returns {boolean} True if signing is complete
     */
    isSigningComplete(status) {
        const { FINALIZED, CANCELLED } = DocumentSignerComponent.DOCUMENT_STATUS;
        return status === FINALIZED || status === CANCELLED;
    }

    /**
     * Handle completion of signing process
     */
    handleSigningComplete() {
        // Close modal
        const modal = document.getElementById(`myModal_${this.id}`);
        if (modal) {
            modal.style.display = "none";
        }
        
        // Update callback
        this.eSignerFinalizeCallback?.();
        
        // Update component state
        this.signCompleted = true;
        
        // Regenerate HTML with signed status
        this.updateDocumentListForSigned();
        this.redraw();
    }

    /**
     * Update document list HTML for signed document
     */
    updateDocumentListForSigned() {
        const form = this.form || this;
        const file = form._data[this.component.documentsList][this.component.index];
        const type = this.getType(file);
        const name = this.getName();
        const { url, icon } = this.getUrl(file, type);
        
        // Create a signed version of the file object
        const signedFile = { ...file, status: DocumentSignerComponent.DOCUMENT_STATUS.SIGNED };
        
        this.documentsList = this.generateDocumentHTML(signedFile, type, url, icon, name);
    }

    render(children) {
        var documentsList = this.documentsList ?
            this.documentsList :
            `<p>not working</p>`;
        setTimeout(() => {
            this.bindHandlers();
        }, 2000);
        var row = `<button id="sign_btn_` + this.id + `" >Sign Form</button>`;
        row += `<div id="myModal_` + this.id + `" class="insuresign modal"></div>`;
        return super.render(`<div>${documentsList}</div>`);
    }

    static editForm = editForm;

    elementInfo() {
        const info = super.elementInfo();
        info.type = "input";
        info.attr.type = "hidden";
        info.changeEvent = "change";
        return info;
    }
    
    build(element) {
        super.build(element);
    }
}